/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import req from '@sportninja/common/api/request'
import colors from '@sportninja/common/constants/appColors'
import { useContext, useEffect } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { RegistrationContext } from './context/RegistrationContext'
import { Main } from './steps'
import { RegistrationSteps } from '@sportninja/common/types'
import EmptyRegistration from './EmptyRegistration'
import LoadingRegistration from './LoadingRegistration'
import RegistrationNotOpen from './RegistrationNotOpen'
import { getSelectedForm } from '../RegistrationPreview/utils'
import { restoreFormAnswers } from '../../utils/restoreFormAnswers'
import { useRegistrationPeriod } from '@sportninja/common/hooks/useRegistrationPeriod'
import {
  slugForServerStep,
  slugForStep,
  registrationStepPath,
} from './registrationSteps'
import { stripServerTrace } from '@sportninja/common/utils/stripServerTrace'
export const REGISTRATION_STEPS = {
  SELECT_YOUR_DIVISION: 0,
  SIGN_UP: 1,
  LOGIN: 2,
  TEAM_REGISTER: 3,
  REGISTRATION_PAYMENT: 4,
  CONFIRM_YOUR_EMAIL: 5,
  REGISTER_REVIEW: 6,
  SELECT_YOUR_ROLE: 7,
  WAIVER: 8,
}

const RegistrationInvite = ({
  login,
  logout,
  isLogged,
  genericSportProperties,
}) => {
  const { id } = useParams()
  const history = useHistory()
  const {
    session,
    setSession,
    registration,
    setZipCode,
    setCountry,
    setBirthDate,
    setDivision,
    setCouponInfo,
    setAdditionalTeamOfficials,
    userId,
    setPromoCode,
    setCredentials,
    playerType,
    teamOfficialType,
    setRole,
    validateCoupon,
    setSelectedPlayer,
    selectedTeam,
    setSelectedTeam,
    players,
    userTeams,
    types,
    setCity,
    setEmergencyEmail,
    setOtherMembershipName,
    setOtherMembershipNumber,
    // Player Info
    setFirstName,
    setLastName,
    setPassword,

    // Address
    setAddress,
    setStateProvince,

    // Parents/Guardian Info
    setParentFirstName,
    setParentLastName,
    setParentEmail,
    setParentPhone,

    // Emergency Contact Info
    setEmergencyFirstName,
    setEmergencyLastName,
    setEmergencyPhone,
    setParentRelationship,
    setDoctorName,
    setDoctorPhone,
    setAllergies,
    setMedicalConditions,

    // Preferences
    setPreferredPosition,
    setJerseyNumber,
    setDominantSide,
    setJerseySize,
    setTshirtSize,
    setShortSize,
    setHockeyUSANumber,
    setHockeyCanadaNumber,
    setNotes,
    error,
    setError,
    loading,
    setLoading,
    setId,
    fetchCountries,
    countries,
    refetchPlayers,
    refetchTeamOfficials,
    fetchTypes,
    typesError,
    countriesError,
    fetchUserInfo,
    userInfo,
    setAnswers,
  } = useContext(RegistrationContext)

  const isRegistrationLive = registration?.registration?.is_live

  const sessionIdFromUrl =
    new URLSearchParams(window.location.search).get(
      'registration_session_uid'
    ) ||
    new URLSearchParams(window.location.search).get('registration_session_id')

  // Set by "New Registration" / "Start Over" when opening a fresh registration in
  // a new tab. `registration_session_<id>` lives in localStorage, which every tab
  // on this origin shares, so without this the new tab would rehydrate and resume
  // the very session the user just chose to leave.
  const forceNewSession =
    new URLSearchParams(window.location.search).get('nreg') === '1'

  // 🔄 useRegistrationPeriod: Determine if registration is open based on user's local timezone
  const { status: registrationStatus, message: registrationPeriodMessage } =
    useRegistrationPeriod(
      registration?.registration?.starts_at,
      registration?.registration?.ends_at
    )

  useEffect(() => {
    if (!isLogged) {
      return
    }
    fetchUserInfo()
  }, [isLogged])

  useEffect(() => {
    if (typesError) {
      setError(typesError)
      return
    }
    if (!types || types === null) {
      setError(null)
      fetchTypes()
    }
  }, [fetchTypes, types, typesError])

  useEffect(() => {
    if (countriesError) {
      setError(countriesError)
      return
    }
    if (countries.length === 0) {
      setError(null)
      fetchCountries()
    }
  }, [countries.length, countriesError])

  // Effects
  useEffect(() => {
    if (userId) {
      refetchPlayers()
    }
  }, [refetchPlayers, userId])

  useEffect(() => {
    if (userId) {
      refetchTeamOfficials()
    }
  }, [refetchTeamOfficials, userId])

  useEffect(() => {
    setId(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  useEffect(() => {
    if (session?.team_id && !selectedTeam && userTeams) {
      const sTeam = userTeams?.find((t) => t?.uid === session?.team_id)
      if (sTeam) {
        setSelectedTeam(sTeam)
      }
    }
  }, [userTeams, session, selectedTeam])

  useEffect(() => {
    const createSession = async () => {
      try {
        setError(null)
        setLoading(true)
        const { data } = await req(`/registration/${id}/session/`, {
          method: 'POST',
        })
        localStorage.setItem(
          `registration_session_${id}`,
          JSON.stringify(data?.id)
        )
        setSession(data)
        // A brand-new session starts at the first step. Reconcile the URL to a
        // valid slug so Main (which renders purely from the URL) shows content —
        // the bare /registration/:id route has no :step and would render blank.
        history.replace(
          registrationStepPath(id, REGISTRATION_STEPS.SELECT_YOUR_DIVISION)
        )
      } catch (e) {
        // Surface the API's own message, minus the PHP file/line suffix it
        // appends (e.g. "... in /var/www/html/.../Foo.php:57"), which is noise
        // to the visitor and leaks server paths.
        const apiMessage = stripServerTrace(e?.message)
        setError(
          new Error(apiMessage || `Registration not found for id: ${id}`)
        )
      } finally {
        setLoading(false)
      }
    }
    const setSessionData = async (data) => {
      // Reconcile the URL to the server's step. The API is the source of truth;
      // Main renders from whatever slug the URL ends up on.
      //
      // The `select_division` server step is a two-way branch (mirrors the prior
      // setSessionData switch): if the user already picked a division AND is a
      // validated logged-in user, jump forward to select-role; otherwise send
      // them to confirm-email (the email-confirmation gate), NOT back to
      // select-division. All other server steps map directly via
      // slugForServerStep.
      let targetSlug = slugForServerStep(data?.registration_step)
      if (data?.registration_step === RegistrationSteps.SELECT_YOUR_DIVISION) {
        targetSlug =
          data?.division_id && userId && userInfo?.is_validated
            ? slugForStep(REGISTRATION_STEPS.SELECT_YOUR_ROLE)
            : slugForStep(REGISTRATION_STEPS.CONFIRM_YOUR_EMAIL)
      }
      history.replace(`/registration/${id}/${targetSlug}`)
      if (data?.player_id) {
        setSelectedPlayer(players?.find((p) => p?.id === data?.player_id))
      }
      if (String(data?.registration_type_id) === String(playerType?.id)) {
        setRole(playerType)
      } else if (
        String(data?.registration_type_id) === String(teamOfficialType?.id)
      ) {
        setRole(teamOfficialType)
      }
      if (data?.coupon_code) {
        setCouponInfo(data?.coupon_code)
        setPromoCode(data?.coupon_code?.code)
        await validateCoupon(
          data?.coupon_code?.code,
          data?.division_id,
          data?.registration_type_id
        )
      }
      if (data?.postal_code) {
        setZipCode(data?.postal_code)
      }
      if (data?.country_code) {
        setCountry(data?.country_code)
      }
      if (data?.division_id) {
        const selectedDivision = registration?.divisions?.find((d) => {
          return d?.schedule_id === data?.division_id
        })
        setDivision(selectedDivision)
      }
      // We should set the additional team officials if they exist
      // the first one is the main team official, the logged in user
      if (data?.team_official_array && data?.team_official_array.length > 1) {
        const teamOfficials = data?.team_official_array
          .map((official) => {
            return {
              email: official?.email,
              firstName: official?.name_first,
              lastName: official?.name_last,
              teamOfficialTypeId: official?.team_official_type_id,
              isTreasurer: official?.is_treasurer,
              phone: official?.phone,
            }
          })
          .slice(1)
        setAdditionalTeamOfficials(teamOfficials)
      }
      if (data?.player_array && data?.player_array.length > 0) {
        const player = data?.player_array[0]
        setFirstName(player?.name_first)
        setLastName(player?.name_last)
        setPassword(player?.password)

        setAddress(player?.address)
        setZipCode(player?.postal_code)
        setStateProvince(player?.state_province)
        setCountry(player?.country)
        setBirthDate(player?.birth_date?.split('T')[0] || null)

        setParentFirstName(player?.parent_first_name)
        setParentLastName(player?.parent_last_name)
        setParentEmail(player?.parent_email)
        setParentPhone(player?.parent_phone_number)

        setEmergencyFirstName(player?.emergency_first_name)
        setEmergencyLastName(player?.emergency_last_name)
        setEmergencyPhone(player?.emergency_phone_number)
        setParentRelationship(player?.emergency_relationship)
        setDoctorName(player?.doctor_name)
        setDoctorPhone(player?.doctor_phone_number)
        setAllergies(player?.known_allergies)
        setMedicalConditions(player?.medical_conditions)

        setPreferredPosition(player?.player_type_id)
        setJerseyNumber(player?.preferred_jersey_number)
        setDominantSide(player?.preferred_dominant_side)
        setJerseySize(player?.preferred_jersey_size)
        setTshirtSize(player?.preferred_tshirt_size)
        setShortSize(player?.preferred_short_size)
        setHockeyUSANumber(player?.hockey_usa_number)
        setHockeyCanadaNumber(player?.hockey_canada_number)
        setNotes(player?.additional_comments_notes)

        setCity(player?.city)
        setEmergencyEmail(player?.emergency_email)
        setOtherMembershipName(player?.custom_membership_name)
        setOtherMembershipNumber(player?.custom_membership_number)
      }

      /**
       * 🔄 DynamicForm restoration (v2 only)
       *
       * Restores form answers from persisted session data for registration v2.
       * This allows users to resume their registration with previously entered
       * dynamic form data intact.
       *
       * @description Only runs when:
       * - registration_version === 2 (new dynamic registration)
       * - session contains form data (data.form exists)
       * - form definition is available for the current role
       *
       * @see restoreFormAnswers - Utility that performs the actual restoration
       * @since Registration v2 - Dynamic form session restoration
       */
      try {
        const isV2 =
          registration?.registration?.root_schedule?.registration_version === 2
        if (isV2 && data?.form) {
          console.log(
            '🔄 setSessionData: Attempting to restore DynamicForm answers',
            {
              hasFormData: !!data.form,
              formSections: Object.keys(data.form || {}),
              registrationVersion:
                registration?.registration?.root_schedule?.registration_version,
            }
          )

          // Determine form type based on role
          const currentRole =
            String(data?.registration_type_id) === String(teamOfficialType?.id)
              ? teamOfficialType
              : playerType
          const formType =
            currentRole?.id === teamOfficialType?.id ? 'team' : 'player'

          // Get the form definition for the current role
          const formDef = getSelectedForm(registration, formType)

          if (formDef) {
            console.log(
              '🔄 setSessionData: Form definition found, restoring answers',
              {
                formId: formDef.id,
                formType,
                groupCount: formDef.groups?.length || 0,
              }
            )

            // Restore the form answers from session data
            const restored = restoreFormAnswers(formDef, data.form)

            // Overwrite all answers atomically to avoid merging stale state
            setAnswers(() => restored)

            console.log(
              '🔄 setSessionData: DynamicForm answers restored successfully',
              {
                restoredFieldCount: Object.keys(restored).length,
                restoredFields: Object.keys(restored),
              }
            )
          } else {
            console.log(
              '🔄 setSessionData: No form definition found for role',
              {
                formType,
                roleId: currentRole?.id,
                roleName: currentRole?.name,
              }
            )
          }
        } else {
          console.log('🔄 setSessionData: Skipping DynamicForm restoration', {
            isV2,
            hasFormData: !!data?.form,
            reason: !isV2 ? 'Not registration v2' : 'No form data in session',
          })
        }
      } catch (restoreErr) {
        console.error(
          '❌ setSessionData: Failed to restore DynamicForm answers',
          {
            error: restoreErr,
            sessionData: data,
            registrationVersion:
              registration?.registration?.root_schedule?.registration_version,
          }
        )
        // Don't throw - restoration failure shouldn't break the session loading
      }
    }
    const fetchPreviousSession = async (sessionId) => {
      try {
        setError(null)
        setLoading(true)
        const { data } = await req(`/registration/session/${sessionId}`)
        if (
          data?.root_schedule_id !==
          registration?.registration?.root_schedule_id
        ) {
          localStorage.removeItem(`registration_session_${id}`)
          createSession()
          return
        }
        setSession(data)
        setSessionData(data)
        if (data?.bearer_token && data?.user) {
          setCredentials(data?.bearer_token, data?.user)
        }
      } catch (e) {
        localStorage.removeItem(`registration_session_${id}`)
        // Deep-linked into a step URL but the session is gone (new tab / expired):
        // strip the slug so we restart cleanly at the base route.
        history.replace(`/registration/${id}`)
        createSession()
      } finally {
        setLoading(false)
      }
    }
    // Outside the registration window the API rejects session creation with a
    // 422, which would surface as an error screen on top of the countdown or
    // closed screen. Only reach for a session once registration is actually open.
    const canOpenSession = isRegistrationLive || registrationStatus === 'active'

    if (
      sessionIdFromUrl &&
      !session &&
      registration &&
      types &&
      canOpenSession &&
      (!isLogged || (isLogged && players !== null))
    ) {
      fetchPreviousSession(sessionIdFromUrl)
    } else if (
      registration &&
      !session &&
      canOpenSession &&
      (!isLogged || (isLogged && players !== null)) &&
      types
    ) {
      const previousSession = forceNewSession
        ? null
        : localStorage.getItem(`registration_session_${id}`)
      if (!previousSession) {
        createSession()
      } else {
        fetchPreviousSession(previousSession.split('"').join(''))
      }
    }
  }, [
    id,
    registration,
    session,
    userId,
    sessionIdFromUrl,
    forceNewSession,
    types,
    players,
    playerType,
    teamOfficialType,
    isRegistrationLive,
    registrationStatus,
  ])

  return (
    <div
      css={css`
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100%;
        overflow-y: scroll;
        /* Adjustment for the Background issue */
        background-color: ${colors.NEUTRAL_900};
      `}
    >
      {loading && !error ? <LoadingRegistration /> : null}
      {error ? <EmptyRegistration errorMessage={error?.message} /> : null}
      {!isRegistrationLive && registrationStatus === 'before' ? (
        <RegistrationNotOpen
          startsAt={registration?.registration?.starts_at}
          name={registration?.registration?.name}
        />
      ) : null}
      {!isRegistrationLive && registrationStatus === 'after' ? (
        <EmptyRegistration
          title='Registration is closed'
          errorMessage={registrationPeriodMessage}
        />
      ) : null}
      {!loading &&
      !error &&
      (isRegistrationLive || registrationStatus === 'active') &&
      session ? (
        <Main />
      ) : null}
    </div>
  )
}

export default RegistrationInvite
